import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';
import { useMemo } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';

type Step = {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  text: string;
};

export default function HowItWorksScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const STEPS: Step[] = useMemo(() => [
    { icon: 'paper-plane-outline', title: t('howItWorks.step1_title'), text: t('howItWorks.step1_text') },
    { icon: 'checkmark-done-outline', title: t('howItWorks.step2_title'), text: t('howItWorks.step2_text') },
    { icon: 'star-outline', title: t('howItWorks.step3_title'), text: t('howItWorks.step3_text') },
    { icon: 'alert-circle-outline', title: t('howItWorks.step4_title'), text: t('howItWorks.step4_text') },
    { icon: 'eye-off-outline', title: t('howItWorks.step5_title'), text: t('howItWorks.step5_text') },
    { icon: 'eye-outline', title: t('howItWorks.step6_title'), text: t('howItWorks.step6_text') },
    { icon: 'shield-checkmark-outline', title: t('howItWorks.step7_title'), text: t('howItWorks.step7_text') },
    { icon: 'document-text-outline', title: t('howItWorks.step8_title'), text: t('howItWorks.step8_text') },
  ], [t]);

  return (
    <ScrollView
      style={styles.flex}
      contentContainerStyle={[
        styles.content,
        { paddingTop: insets.top + spacing.lg, paddingBottom: insets.bottom + spacing.xl },
      ]}
    >
      <View style={styles.topBar}>
        <Pressable onPress={() => router.back()} hitSlop={12} accessibilityRole="button">
          <Text style={styles.back}>{t('howItWorks.back')}</Text>
        </Pressable>
        <Text style={styles.title}>{t('howItWorks.title')}</Text>
        <View style={styles.spacer} />
      </View>

      <Text style={styles.intro}>
        {t('howItWorks.intro')}
      </Text>

      {STEPS.map((step, i) => (
        <View key={i} style={styles.step}>
          <View style={styles.stepIcon}>
            <Ionicons name={step.icon} size={22} color={colors.primary} />
          </View>
          <View style={styles.stepBody}>
            <Text style={styles.stepTitle}>{step.title}</Text>
            <Text style={styles.stepText}>{step.text}</Text>
          </View>
        </View>
      ))}
    </ScrollView>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: colors.background },
    content: {
      paddingHorizontal: spacing.lg,
      gap: spacing.lg,
    },
    topBar: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    back: { color: colors.primaryLight, fontSize: fontSize.md, fontWeight: '600' },
    title: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700' },
    spacer: { width: 60 },
    intro: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      lineHeight: 22,
    },
    step: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: spacing.md,
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      padding: spacing.md,
      borderWidth: 1,
      borderColor: colors.border,
    },
    stepIcon: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: colors.surfaceAlt,
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    stepBody: { flex: 1, gap: 4 },
    stepTitle: { color: colors.text, fontSize: fontSize.md, fontWeight: '700' },
    stepText: { color: colors.textMuted, fontSize: fontSize.sm, lineHeight: 19 },
  });
}
